import React, { useCallback, useEffect, useRef, useState } from 'react'
import styled from 'styled-components'
import SearchIcon from '@mui/icons-material/Search'
import ClearIcon from '@mui/icons-material/Clear'
import useDebounce from 'utils/hooks/useDebounce'
import { isEmpty } from 'ramda'
import { useDispatch, useSelector } from 'react-redux'
import {
  searchBpsRoutine,
  searchFilesRoutine
} from 'features/bpList/ducks/actions'
import {
  getSearchBpResults,
  getSearchFileResults
} from 'features/bpList/ducks/selectors'
import SearchBpListItem from 'features/SearchInput/components/SearchBpListItem'
import {
  getLastSearchedBps,
  getLastSearchedFiles
} from 'features/dashboard/ducks/selectors'
import { markBpAsSearchedRoutine } from 'features/bpDetails/ducks/actions'
import {
  fetchLastSearchedBpsRoutine,
  fetchLastSearchedFilesRoutine
} from 'features/dashboard/ducks/actions'
import { isNotNilOrEmpty } from 'utils/ramda'
import { useNavigate } from 'react-router-dom'
import SearchFileListItem from 'features/SearchInput/components/SearchFileListItem'
import CloseIcon from '@mui/icons-material/Close'
import useBodyScrollLock from 'hooks/useBodyScrollLock'
import CircularProgress from '@mui/material/CircularProgress'
import SearchableSelect from 'components/SearchableSelect'

export const SearchInput = ({
  disabled = false,
  icon = SearchIcon,
  placeholder = 'Search my Building Passports...',
  showBoxClose = true,
  showClear = true,
  ...props
}) => {
  const [value, setValue] = useState('')
  const [boxOpen, setBoxOpen] = useState(false)
  const [searchType, setSearchType] = useState('buildingpassports')
  const [isMobile, setIsMobile] = useState(false)
  const [mobileBoxStyle, setMobileBoxStyle] = useState(null)
  const inputBoxRef = useRef(null)
  const inputWrapperRef = useRef(null)
  const lastQueryRef = useRef('')
  const dispatch = useDispatch()
  const debouncedValue = useDebounce(value, 500)
  const lastSearchedBps = useSelector(getLastSearchedBps)
  const lastSearchedFiles = useSelector(getLastSearchedFiles)
  const userBps = useSelector(getSearchBpResults)
  const userFiles = useSelector(getSearchFileResults)
  const [results, setResults] = useState([])
  const hasResults = isNotNilOrEmpty(results)
  const searchIconOverrides = { fontSize: 25 }
  const handleClearValue = () => setValue('')
  const handleInputChange = e => setValue(e.target.value)
  const MIN_QUERY_LENGTH = 3
  const isSearching = debouncedValue.length >= MIN_QUERY_LENGTH
  const [isLoading, setIsLoading] = useState(false)
  const isDebouncing = value.length >= MIN_QUERY_LENGTH && debouncedValue !== value
  const navigate = useNavigate()

  const handleBoxOpen = () => setBoxOpen(true)
  const handleBoxClose = () => setTimeout(() => setBoxOpen(false), 200)

  const handleSearchTypeChange = val => {
    setSearchType(val)
    setValue('') // Clear input on search type change
    setResults([])
  }

  const handleClickBp = bp => () => {
    setValue('')
    handleBoxClose()
    dispatch(markBpAsSearchedRoutine({ bpId: bp.id }))
    window.location
    setTimeout(() => {
      navigate(`/bp-list/${bp.id}`, { replace: true })
      window.location.reload()
    }, 0)
  }

  useEffect(() => {
    dispatch(fetchLastSearchedBpsRoutine())
    dispatch(fetchLastSearchedFilesRoutine())
  }, [])

  useEffect(() => {
    if (typeof window === 'undefined') return
    const updateIsMobile = () => {
      setIsMobile(window.innerWidth <= 768)
    }
    updateIsMobile()
    window.addEventListener('resize', updateIsMobile)
    return () => window.removeEventListener('resize', updateIsMobile)
  }, [])

  const updateMobileBoxStyle = useCallback(() => {
    if (!inputWrapperRef.current || typeof window === 'undefined') return
    const rect = inputWrapperRef.current.getBoundingClientRect()
    const top = Math.max(0, Math.round(rect.bottom + 6))
    const maxHeight = Math.max(120, window.innerHeight - top - 12)
    setMobileBoxStyle({
      top: `${top}px`,
      maxHeight: `${maxHeight}px`,
    })
  }, [])

  useEffect(() => {
    if (!boxOpen || !isMobile) return
    updateMobileBoxStyle()
    window.addEventListener('resize', updateMobileBoxStyle)
    window.addEventListener('scroll', updateMobileBoxStyle, { passive: true })
    const rafId = window.requestAnimationFrame(updateMobileBoxStyle)
    return () => {
      window.cancelAnimationFrame(rafId)
      window.removeEventListener('resize', updateMobileBoxStyle)
      window.removeEventListener('scroll', updateMobileBoxStyle)
    }
  }, [boxOpen, isMobile, updateMobileBoxStyle])

  useBodyScrollLock(boxOpen && isMobile, { allowSticky: true, allowScrollRef: inputBoxRef })

  useEffect(() => {
    if (props.autoFocus && !disabled) {
      setBoxOpen(true)
    }
  }, [props.autoFocus, disabled])

  useEffect(() => {
    if (!boxOpen || !isMobile) return

    const handlePointerDown = event => {
      const target = event.target
      if (!inputWrapperRef.current || !inputBoxRef.current) return
      if (inputWrapperRef.current.contains(target)) return
      if (inputBoxRef.current.contains(target)) return
      handleBoxClose()
    }

    document.addEventListener('pointerdown', handlePointerDown, true)
    return () => document.removeEventListener('pointerdown', handlePointerDown, true)
  }, [boxOpen, isMobile])

  useEffect(() => {
    if (searchType === 'buildingpassports') {
      isSearching ? setResults(userBps) : setResults(lastSearchedBps)
    } else if (searchType === 'files') {
      isSearching ? setResults(userFiles) : setResults(lastSearchedFiles)
    }
  }, [
    lastSearchedBps,
    userBps,
    lastSearchedFiles,
    userFiles,
    isSearching,
    searchType
  ])

  useEffect(() => {
    if (!isSearching) return

    const payload = { query: debouncedValue, exactMatch: false }
    lastQueryRef.current = debouncedValue
    setIsLoading(true)

    if (searchType === 'buildingpassports') {
      dispatch(searchBpsRoutine(payload))
    } else if (searchType === 'files') {
      dispatch(searchFilesRoutine(payload))
    }
  }, [debouncedValue, searchType])

  useEffect(() => {
    if (!isSearching) {
      setIsLoading(false)
      return
    }

    if (lastQueryRef.current !== debouncedValue) return

    if (searchType === 'buildingpassports') {
      if (userBps) setIsLoading(false)
    } else if (searchType === 'files') {
      if (userFiles) setIsLoading(false)
    }
  }, [userBps, userFiles, searchType, isSearching, debouncedValue])

  const dynamicPlaceholder =
    searchType === 'buildingpassports'
      ? 'Search my Building Passports...'
      : 'Search my Files...'

  return (
    <SearchInputContainer disabled={disabled}>
      <InputWrapper onClick={handleBoxOpen} ref={inputWrapperRef}>
        <SearchInputIcon>
          <StyledInputIcon style={searchIconOverrides} />
        </SearchInputIcon>
        <ScopeField>
          <SearchableSelect
            value={searchType}
            onChange={handleSearchTypeChange}
            options={[
              { value: 'buildingpassports', label: 'Building Passports' },
              { value: 'files', label: 'Files' },
            ]}
            disabled={disabled}
            ariaLabel='Select search type'
          />
        </ScopeField>
        <StyledInput
          {...props}
          type="text"
          placeholder={dynamicPlaceholder}
          disabled={disabled}
          value={value}
          onChange={handleInputChange}
          onFocus={handleBoxOpen}
        />
        {showClear && !isEmpty(value) && (
          <StyledClearIcon onClick={handleClearValue} />
        )}
      </InputWrapper>

      {boxOpen && (
        <SearchBox ref={inputBoxRef} style={isMobile ? mobileBoxStyle : undefined}>
          <BoxHeader>
            <BoxTitle>
              {isSearching
                ? (isLoading || isDebouncing)
                  ? 'Searching...'
                  : `Results (${results?.length || 0})`
                : 'Last searched'}
            </BoxTitle>
            {showBoxClose && (
              <CloseButton onClick={handleBoxClose}>
                <StyledCloseIcon />
              </CloseButton>
            )}
          </BoxHeader>
          <ResultsList>
            {(isLoading || isDebouncing) ? (
              <LoadingRow>
                <CircularProgress size={18} sx={{ color: '#33becc' }} />
                <LoadingText>Searching...</LoadingText>
              </LoadingRow>
            ) : hasResults ? (
              results.map(item =>
                searchType === 'buildingpassports' ? (
                  <SearchBpListItem
                    disabled={item.status === 'dormant'}
                    onClick={handleClickBp(item)}
                    bp={item}
                    key={`bp-${item.id}`}
                  />
                ) : (
                  <SearchFileListItem file={item} key={`file-${item.id}`} />
                )
              )
            ) : isSearching ? (
              <NoResultsInfo>No results matching search criteria</NoResultsInfo>
            ) : (
              <NoResultsInfo>No previous search history to display</NoResultsInfo>
            )}
          </ResultsList>
        </SearchBox>
      )}
    </SearchInputContainer>
  )
}

export default SearchInput

const StyledInput = styled.input`
  border: none;
  background-image: none;
  background-color: transparent;
  box-shadow: none;
  line-height: 16px;
  font-size: 16px;
  flex-grow: 2;
  padding-right: 20px;
  height: 100%;
  min-width: 0;

  @media (max-width: 768px) {
    font-size: 14px;
  }
`

const ScopeField = styled.div`
  flex-shrink: 0;
  width: 185px;
  margin-top: 2px;
  margin-right: 10px;

  @media (max-width: 768px) {
    width: 150px;
    margin-right: 6px;
  }

  @media (max-width: 480px) {
    width: 120px;
    margin-right: 4px;
  }
`

const SearchInputContainer = styled.div`
  border-radius: 4px;
  height: 100%;
  box-sizing: border-box;
  opacity: ${props => (props.disabled ? '.5' : '1')};
  width: 100%;
  display: flex;
  position: relative;

  @media (max-width: 768px) {
    height: auto;
  }
`

const InputWrapper = styled.div`
  width: 100%;
  display: flex;
  align-items: center;
  position: relative;
  padding: 0 30px;

  @media (max-width: 768px) {
    padding: 0 12px;
  }

  @media (max-width: 480px) {
    padding: 0 8px;
  }
`

const SearchInputIcon = styled.div`
  position: relative;
  top: 2px;
  margin-right: 15px;
  color: rgba(152, 152, 152, 0.61);
  align-items: center;

  @media (max-width: 768px) {
    display: none;
    margin-right: 0;
  }
`

const StyledInputIcon = styled(SearchIcon)`
  font-size: 25px !important;
`

const StyledClearIcon = styled(ClearIcon)`
  cursor: pointer;
  position: absolute;
  right: 65px;
  padding: 10px;
  font-size: 16px;
  color: rgba(152, 152, 152, 0.61);

  @media (max-width: 1024px) {
    display: none;
  }
`

const CloseButton = styled.button`
  background: transparent;
  border: none;
  cursor: pointer;
  padding: 0;
  display: flex;
  align-items: center;

  @media (max-width: 768px) {
    display: none;
  }
`

const SearchBox = styled.div`
  width: 1400px;
  max-height: 400px;
  overflow-y: auto;
  padding: 24px 32px;
  position: absolute;
  top: 60px;
  margin-left: -30px;
  box-shadow: rgba(0, 0, 0, 0.16) 0px 2px 10px;
  background-color: rgb(250, 251, 252);
  border-width: 2px 1px 1px;
  border-style: solid;
  border-color: rgb(51, 190, 204) rgb(230, 230, 230) rgb(230, 230, 230);
  border-image: initial;
  box-sizing: border-box;

  table {
    width: 100%;
    border-collapse: collapse;
  }

  @media (max-width: 768px) {
    position: fixed;
    top: 60px;
    left: 0;
    right: 0;
    width: 100%;
    margin-left: 0;
    max-height: calc(100vh - 80px);
    padding: 16px;
    background-color: #fff;
    border-radius: 0;
    z-index: 200;
    overflow-x: hidden;
    -webkit-overflow-scrolling: touch;


    & > div:nth-child(2) {
      & > div {
        & > div:nth-child(n + 3) {
          display: none !important;
        }
        & > div > div:nth-child(n + 3) {
          display: none !important;
        }
      }
    }
  }

  @media (max-width: 480px) {
    padding: 12px;
  }
`

const BoxTitle = styled.div`
  padding-bottom: 10px;
  width: auto;
  border-bottom: 1px solid ${({ theme }) => theme.colors.grey[500]};
  color: ${({ theme }) => theme.colors.haiti};
  opacity: 60%;
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 7px;

  @media (max-width: 768px) {
    font-size: 11px;
  }
`

const ResultsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;

  @media (max-width: 768px) {
    gap: 10px;
  }
`

const NoResultsInfo = styled.div`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.haiti};

  @media (max-width: 768px) {
    font-size: 11px;
  }
`

const InlineLoader = styled.div`
  display: flex;
  align-items: center;
  margin-left: auto;
  margin-right: 8px;
`

const LoadingRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  padding: 18px 0;
`

const LoadingText = styled.span`
  font-size: 13px;
  font-weight: 500;
  color: #1a8a96;
`

const BoxHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 10px;
`

const StyledCloseIcon = styled(CloseIcon)`
  font-size: 16px !important;
  color: ${({ theme }) => theme.colors.haiti};
  cursor: pointer !important;

  &:hover {
    color: ${({ theme }) => theme.colors.black};
  }
`
